import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';

import { ReactComponent as EditIcon } from '@repo/common/assets/icons/16px/icon_16px_edit_outline.svg';
import { ReactComponent as PdfIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bigger.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/16px/icon_16px_delete_outline.svg';
import { ReactComponent as VisibleIcon } from '@repo/common/assets/icons/16px/icon_16px_pin-left_outline.svg';

import {
  BaseButton,
  Modal,
  useIsMobile,
  FilterBottomSheet,
  useT,
} from '@repo/common';
import PreviewLargerModal from '../Preview/PreviewLargerModal';
import { useResumePdfDownload } from '../../../utils/useResumePdfDownload';

import {
  deleteCareer,
  setStandardCareer,
} from '../../../api/career/career.api';

function ResumeCardOptionList({
  anchorRect,
  resumeData,
  onDeleted,
  onStandardSuccess,
  onDeleteSuccess,
  onClose,
  isMyPage,
  isAllPage,
}) {
  const t = useT();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const listRef = useRef(null);
  const [activeItem, setActiveItem] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isStandardModalOpen, setIsStandardModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteStandardModalOpen, setIsDeleteStandardModalOpen] =
    useState(false);

  const { handleDownload } = useResumePdfDownload(resumeData, resumeData.title);

  /*
   * 모달이 열려 있으면 스크롤로 메뉴를 닫지 않는다.
   * 모달은 열릴 때 body 스크롤을 잠그는데, 페이지가 스크롤된 상태(커리어 페이지 My Resumes)면
   * 이때 scroll 이벤트가 나서 메뉴와 함께 방금 연 모달까지 닫혀버린다.
   */
  const isAnyModalOpen =
    isViewModalOpen ||
    isStandardModalOpen ||
    isDeleteModalOpen ||
    isDeleteStandardModalOpen;

  useEffect(() => {
    if (isAnyModalOpen || isMobile) return;

    const handleScroll = () => onClose?.();
    window.addEventListener('scroll', handleScroll, true);

    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [onClose, isAnyModalOpen, isMobile]);

  if (!anchorRect && !isMobile) return null;

  const handleItemClick = (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveItem(action);

    if (action === 'edit') handleEditClick();
    if (action === 'view') handleViewClick();
    if (action === 'standard') handleStandardClick();
    if (action === 'delete') handleDeleteClick();
  };

  const handleEditClick = () => {
    onClose?.();
    navigate(`/career/resumes/${resumeData.id}`, {
      state: {
        resumeData,
        resumeTitle: resumeData.title,
        mode: 'edit',
      },
    });
  };

  const handleViewClick = async () => {
    setIsViewModalOpen(true);
  };

  const handleStandardClick = () => {
    setIsStandardModalOpen(true);
  };

  const handleCancelStandard = () => {
    setIsStandardModalOpen(false);
  };

  const handleConfirmStandard = async () => {
    try {
      await setStandardCareer(resumeData.id);

      setIsStandardModalOpen(false);
      onClose?.();
      onDeleted?.();
      onStandardSuccess?.();
    } catch (e) {
      console.error('대표 이력서 설정 실패');
    }
  };

  const handleDeleteClick = () => {
    if (resumeData.standard) {
      setIsDeleteStandardModalOpen(true);
    } else {
      setIsDeleteModalOpen(true);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setIsDeleteStandardModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteCareer(resumeData.id);
      onClose?.();
      onDeleted?.();
      setIsDeleteModalOpen(false);
      onDeleteSuccess?.();
    } catch (e) {
      console.error('삭제 중 오류 발생');
    }
  };

  const menuItems = (
    <ul className='flex flex-col'>
      <li>
        <BaseButton
          type='text'
          size='m'
          iconPosition='after'
          icon={<EditIcon />}
          width='100%'
          separate={true}
          leftalign={true}
          onClick={(e) => handleItemClick(e, 'edit')}
          bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
            activeItem === 'edit' ? 'bg-grayscale-gray02 text-text-primary' : ''
          }`}
          textclass='typo-b14Strong'
          hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
          activeclass='active:bg-grayscale-gray02 active:text-text-primary'
        >
          {t('Edit', '수정')}
        </BaseButton>
      </li>

      <li>
        <BaseButton
          type='text'
          size='m'
          iconPosition='after'
          icon={<PdfIcon />}
          width='100%'
          separate={true}
          leftalign={true}
          onClick={(e) => handleItemClick(e, 'view')}
          bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
            activeItem === 'view' ? 'bg-grayscale-gray02 text-text-primary' : ''
          }`}
          textclass='typo-b14Strong'
          hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
          activeclass='active:bg-grayscale-gray02 active:text-text-primary'
        >
          {t('View PDF', 'PDF 보기')}
        </BaseButton>
      </li>

      {!isMyPage && (
        <>
          <li>
            <BaseButton
              type='text'
              size='m'
              iconPosition='after'
              icon={<VisibleIcon />}
              width='100%'
              separate={true}
              leftalign={true}
              onClick={(e) => handleItemClick(e, 'standard')}
              bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                activeItem === 'standard'
                  ? 'bg-grayscale-gray02 text-text-primary'
                  : ''
              }`}
              textclass='typo-b14Strong'
              hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
              activeclass='active:bg-grayscale-gray02 active:text-text-primary'
            >
              {t('Set as standard', '대표 이력서로 설정')}
            </BaseButton>
          </li>

          <li>
            <BaseButton
              type='text'
              size='m'
              iconPosition='after'
              icon={<DeleteIcon />}
              width='100%'
              separate={true}
              leftalign={true}
              onClick={(e) => handleItemClick(e, 'delete')}
              bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                activeItem === 'delete'
                  ? 'bg-grayscale-gray02 text-text-primary'
                  : ''
              }`}
              textclass='typo-b14Strong'
              hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
              activeclass='active:bg-grayscale-gray02 active:text-text-primary'
            >
              {t('Delete', '삭제')}
            </BaseButton>
          </li>
        </>
      )}
    </ul>
  );

  return (
    <>
      {ReactDOM.createPortal(
        isMobile
          ? !isViewModalOpen && (
              <FilterBottomSheet onClose={onClose}>
                {menuItems}
              </FilterBottomSheet>
            )
          : anchorRect && !isViewModalOpen && (
              <>
                <div
                  className='fixed inset-0 z-[999999] bg-transparent'
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose?.();
                  }}
                />

                <div
                  ref={listRef}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'fixed',
                    top: isMyPage
                      ? anchorRect.top - 50
                      : isAllPage
                        ? anchorRect.top - 140
                        : anchorRect.top - 180,
                    left: isMyPage
                      ? anchorRect.left + 40
                      : isAllPage
                        ? anchorRect.left + 30
                        : anchorRect.right,
                  }}
                  className='
                  w-[200px]
                  bg-grayscale-gray00 
                  border border-grayscale-gray02 
                  rounded-lg
                  shadow-popup-04
                  z-[1000000]
                '
                >
                  <div className='bg-grayscale-gray00 rounded-full p-2'>
                    {menuItems}
                  </div>
                </div>
              </>
            ),
        document.body,
      )}

      {isViewModalOpen && (
        <PreviewLargerModal
          resumeData={resumeData}
          onClose={() => {
            setIsViewModalOpen(false);
            if (isMyPage) {
              onClose?.();
            }
          }}
          onDownload={handleDownload}
        />
      )}

      {isStandardModalOpen && (
        <Modal
          isOpen
          onClickLeftBtn={handleCancelStandard}
          onClickRightBtn={handleConfirmStandard}
          title={t('Set as standard resume?', '대표 이력서로 설정할까요?')}
          subText={t(
            'This resume will be used when you apply for jobs.\nYou can change your standard resume anytime.',
            '이 이력서는 채용 지원 시 사용됩니다.\n대표 이력서는 언제든지 변경할 수 있습니다.',
          )}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          leftHideIcon={true}
          rightButtonLabel={t('Set as standard', '대표 이력서로 설정')}
          rightHideIcon={true}
        />
      )}

      {isDeleteModalOpen && (
        <Modal
          isOpen
          onClickLeftBtn={handleCancelDelete}
          onClickRightBtn={handleConfirmDelete}
          title={t('Delete this resume?', '이 이력서를 삭제할까요?')}
          subText={t('This action can’t be undone', '삭제 후에는 되돌릴 수 없습니다')}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          leftHideIcon={true}
          rightButtonLabel={t('Delete', '삭제')}
          rightHideIcon={true}
          type='warning'
        />
      )}

      {isDeleteStandardModalOpen && (
        <Modal
          isOpen
          onClickLeftBtn={handleCancelDelete}
          onClickRightBtn={handleConfirmDelete}
          title={t('Delete your standard resume?', '대표 이력서를 삭제할까요?')}
          subText={t(
            'If deleted, your most recently updated\nresume will automatically become the new\nstandard resume',
            '삭제하면 가장 최근에 수정한 이력서가\n자동으로 새 대표 이력서로 설정됩니다',
          )}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          leftHideIcon={true}
          rightButtonLabel={t('Delete', '삭제')}
          rightHideIcon={true}
          type='warning'
        />
      )}
    </>
  );
}

export default ResumeCardOptionList;
